import React, { useEffect, useRef } from "react";
import z from "zod";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";

import { EngagementSettingsSchema } from "@performyard/models";
import {
	Checkbox,
	FormField,
	FormHandle,
	FormSingleSelect,
	FormSubscribe,
	Icon,
	PYForm,
	ResetButton,
	SingleSelectInput,
	SubmitButton,
	Text,
	Tooltip,
	VALIDATE_ON,
	toast,
} from "@performyard/py-components";

import EngagementSettingsSurveySchedule from "../components/EngagementSettingsSurveySchedule";
import { SettingLabel } from "../components/SettingLabel";
import SettingsSection from "../components/SettingsSection";

import { useCompanyEngagementSettings } from "../../../../../../api/hooks/defs/company";
import { useSetCompanyEngagementSettings } from "../../../../../../api/hooks/defs/engagement";
import useUnsavedChanges from "../../../../../../hooks/useUnsavedChanges";

const EngagementQuestionsSchemaSlice = EngagementSettingsSchema.pick({
	factors_frequency: true,
	enps_frequency: true,
	ask_additional_enps_feedback_question: true,
	enps_range: true,
	custom_questions_frequency: true,
	custom_questions_include_all: true,
	additional_feedback_question_frequency: true,
	question_cycle: true,
});

const questionFrequencyOptions: { value: number; label: string }[] = [
	{
		value: 1,
		label: "Every Pulse Survey",
	},
	{
		value: 2,
		label: "Every 2nd Pulse Survey",
	},
	{
		value: 3,
		label: "Every 3rd Pulse Survey",
	},
	{
		value: 4,
		label: "Every 4th Pulse Survey",
	},
];

const enpsQuestionFrequencyOptions: { value: number; label: string }[] = [
	...questionFrequencyOptions,
	{ value: 0, label: "Never" },
];

const customQuestionsFrequencyOptions: { value: number; label: string }[] = [
	...questionFrequencyOptions,
	{ value: 0, label: "Never" },
];

const additionalFeedbackQuestionFrequencyOptions: { value: number; label: string }[] = [
	...questionFrequencyOptions,
	{ value: 0, label: "Never" },
];

const zeroToTenOptions = Array.from(Array(11).keys()).map((index) => ({
	value: index,
	label: index.toString(),
}));

const enpsRangeOptions: { value: number | null; label: string }[] = [{ value: null, label: "-" }, ...zeroToTenOptions];

const FactorsSelectInput = ({
	value,
	onChange,
	enpsFrequency,
}: {
	value: number;
	onChange: (value: number) => void;
	enpsFrequency: number;
}) => {
	useEffect(() => {
		if (enpsFrequency !== 1) {
			onChange(1);
		}
	}, [enpsFrequency]);
	return (
		<SingleSelectInput<number>
			value={value}
			onChange={(value) => onChange(value ?? 1)}
			options={questionFrequencyOptions}
			dangerouslySetClassName="max-w-[197px] ml-auto"
		/>
	);
};

const EnpsSelectInput = ({
	value,
	onChange,
	factorsFrequency,
}: {
	value: number;
	onChange: (value: number) => void;
	factorsFrequency: number;
}) => {
	useEffect(() => {
		if (factorsFrequency !== 1) {
			onChange(1);
		}
	}, [factorsFrequency]);
	return (
		<SingleSelectInput<number>
			value={value}
			onChange={(value) => onChange(value ?? 1)}
			options={enpsQuestionFrequencyOptions}
			dangerouslySetClassName="max-w-[197px] ml-auto"
		/>
	);
};

const QuestionsTab = () => {
	const formRef = useRef<FormHandle<typeof EngagementQuestionsSchemaSlice>>(null);

	const { data: engagementSettings, isLoading } = useCompanyEngagementSettings();

	const { mutate: setCompanyEngagementSettings, isPending: isSettingCompanyEngagementSettings } =
		useSetCompanyEngagementSettings({
			onSuccess: () => {
				toast.success("Changes saved successfully");
				setIsSafe();
				formRef.current?.reset();
			},
		});

	const { setIsUnsafe, setIsSafe } = useUnsavedChanges();

	// Reset form after data loads to ensure it's not dirty on initial load
	useEffect(() => {
		if (engagementSettings && formRef.current) {
			formRef.current.reset();
		}
	}, [isLoading]);

	const handleSubmit = (values: z.infer<typeof EngagementQuestionsSchemaSlice>) => {
		// Check if any frequency changed and reset question_cycle to 1 if so
		const initialValues = engagementSettings?.engagement;
		const shouldResetQuestionCycle =
			values.enps_frequency !== initialValues?.enps_frequency ||
			values.factors_frequency !== initialValues?.factors_frequency ||
			values.custom_questions_frequency !== initialValues?.custom_questions_frequency ||
			values.additional_feedback_question_frequency !== initialValues?.additional_feedback_question_frequency;

		// Cross-field dependency: when enps or factors frequency changes from default,
		// reset the other to 1 as well (matching old Main tab behavior)
		const adjustedValues = { ...values };

		if (values.enps_frequency !== initialValues?.enps_frequency && values.enps_frequency !== 1) {
			adjustedValues.factors_frequency = 1;
		}

		if (values.factors_frequency !== initialValues?.factors_frequency && values.factors_frequency !== 1) {
			adjustedValues.enps_frequency = 1;
		}

		// Special case: if eNPS frequency is set to Never (0),
		// uncheck the additional feedback checkbox
		if (values.enps_frequency === 0) {
			adjustedValues.ask_additional_enps_feedback_question = false;
		}

		// Special case: if custom questions frequency is set to Never (0),
		// restore customQuestionsIncludeAll to its initial value
		if (values.custom_questions_frequency === 0) {
			adjustedValues.custom_questions_include_all = initialValues?.custom_questions_include_all || false;
		}

		// Reset question_cycle to 1 if any frequency changed
		if (shouldResetQuestionCycle) {
			adjustedValues.question_cycle = 1;
		}

		setCompanyEngagementSettings(adjustedValues);
	};

	if (isLoading) return <div>Loading...</div>;

	if (!engagementSettings) throw new Error("No engagement settings found");

	// Prepare default values with proper structure
	const defaultValues = EngagementQuestionsSchemaSlice.parse({
		factors_frequency: engagementSettings?.engagement.factors_frequency || 1,
		enps_frequency: engagementSettings?.engagement.enps_frequency ?? 1,
		ask_additional_enps_feedback_question:
			engagementSettings?.engagement.ask_additional_enps_feedback_question || false,
		enps_range: {
			below: engagementSettings?.engagement.enps_range?.below ?? null,
			above: engagementSettings?.engagement.enps_range?.above ?? null,
		},
		custom_questions_frequency: engagementSettings?.engagement.custom_questions_frequency ?? 0,
		custom_questions_include_all: engagementSettings?.engagement.custom_questions_include_all || false,
		additional_feedback_question_frequency:
			engagementSettings?.engagement.additional_feedback_question_frequency ?? 0,
		question_cycle: engagementSettings?.engagement.question_cycle || 1,
	});

	return (
		<PYForm
			ref={formRef}
			validateOn={[VALIDATE_ON.ON_SUBMIT]}
			schema={EngagementQuestionsSchemaSlice}
			defaultValues={defaultValues}
			onSubmit={({ value }) => {
				handleSubmit(value);
			}}
			id="engagement-questions-form"
		>
			<div className="w-full pt-10" key="form-content">
				<SettingsSection
					title="Pulse Survey Questions"
					description="Set preferences for the types of questions to be included in the employee pulse surveys."
				>
					<div className="space-y-8">
						<FormSubscribe
							id="factorsFrequencySubscribe"
							selector={(state) => ({
								enpsFrequency: state.values.enps_frequency,
							})}
						>
							{({ enpsFrequency }) => {
								return (
									<FormField<number>
										id="factors_frequency"
										labelPosition="left"
										justify="between"
										label={
											<SettingLabel
												title="Ask Factor Questions"
												description="Set how often the Factor questions should be included in employee pulse surveys."
											/>
										}
										input={(field) => {
											return (
												<FactorsSelectInput
													value={field.value}
													onChange={field.onChange}
													enpsFrequency={enpsFrequency}
												/>
											);
										}}
									/>
								);
							}}
						</FormSubscribe>
						<div className="flex flex-col gap-4">
							<FormSubscribe
								id="enpsFrequencySubscribe"
								selector={(state) => ({
									factorsFrequency: state.values.factors_frequency,
								})}
							>
								{({ factorsFrequency }) => {
									return (
										<FormField<number>
											id="enps_frequency"
											labelPosition="left"
											justify="between"
											label={
												<SettingLabel
													title="Ask eNPS Question"
													description="Set how often the eNPS question should be included in employee pulse surveys."
												/>
											}
											input={(field) => (
												<EnpsSelectInput
													value={field.value}
													onChange={field.onChange}
													factorsFrequency={factorsFrequency}
												/>
											)}
										/>
									);
								}}
							</FormSubscribe>

							<FormSubscribe
								id="enpsFrequencyAdditionalFeedbackSubscribe"
								selector={(state) => ({
									enpsFrequency: state.values.enps_frequency,
									askAdditionalFeedback: state.values.ask_additional_enps_feedback_question,
								})}
							>
								{({ enpsFrequency, askAdditionalFeedback }) => {
									return (
										<>
											<FormField<boolean>
												id="ask_additional_enps_feedback_question"
												labelPosition="right"
												disabled={enpsFrequency === 0}
												label={
													<Text
														size="paragraph-3"
														weight="regular"
														color="neutral-9"
														dangerouslySetClassName={
															enpsFrequency === 0 ? "opacity-disabled" : ""
														}
													>
														Ask for Additional eNPS Feedback
													</Text>
												}
												input={(field) => (
													<div className="flex items-center gap-2">
														<Checkbox
															id={`ask_additional_enps_feedback_question-checkbox`}
															checked={enpsFrequency === 0 ? false : field.value}
															onCheckedChange={field.onChange}
															disabled={enpsFrequency === 0}
														/>
													</div>
												)}
											/>

											{askAdditionalFeedback && enpsFrequency !== 0 && (
												<div className="ml-6">
													<FormSubscribe
														id="enpsRangeSubscribe"
														selector={(state) => ({
															rangeBelow: state.values.enps_range?.below,
															rangeAbove: state.values.enps_range?.above,
														})}
													>
														{({ rangeBelow, rangeAbove }) => {
															// Filter "below" options: must be less than "above" value
															const belowOptions = enpsRangeOptions.filter((option) => {
																if (option.value === null) return true;
																if (rangeAbove === null) return true;
																return (option.value as number) < rangeAbove;
															});

															// Filter "above" options: must be greater than "below" value
															const aboveOptions = enpsRangeOptions.filter((option) => {
																if (option.value === null) return true;
																if (rangeBelow === null) return true;
																return (option.value as number) > rangeBelow;
															});

															return (
																<div className="flex items-center justify-between">
																	<div className="max-w-[288px] space-y-2">
																		<Text
																			size="paragraph-3"
																			weight="semi-bold"
																			color="neutral-9"
																		>
																			Set Feedback Ranges
																		</Text>
																		<Text
																			size="paragraph-3"
																			weight="regular"
																			color="neutral-9"
																		>
																			eNPS answers in these ranges will trigger an
																			additional feedback field in your pulse
																			surveys.
																		</Text>
																	</div>
																	<div className="flex flex-col gap-1">
																		<FormSingleSelect<number | null>
																			id="enps_range.below"
																			labelPosition="right"
																			label={
																				<Text
																					size="paragraph-3"
																					weight="regular"
																					color="neutral-9"
																				>
																					And Below
																				</Text>
																			}
																			options={belowOptions}
																			dangerouslySetClassName="max-w-[60px]"
																		/>
																		<FormSingleSelect<number | null>
																			id="enps_range.above"
																			labelPosition="right"
																			label={
																				<Text
																					size="paragraph-3"
																					weight="regular"
																					color="neutral-9"
																				>
																					And Above
																				</Text>
																			}
																			options={aboveOptions}
																			dangerouslySetClassName="max-w-[60px]"
																		/>
																	</div>
																</div>
															);
														}}
													</FormSubscribe>
												</div>
											)}
										</>
									);
								}}
							</FormSubscribe>
						</div>
						<div className="flex flex-col gap-4">
							<FormSingleSelect<number>
								id="custom_questions_frequency"
								labelPosition="left"
								justify="between"
								label={
									<SettingLabel
										title="Ask Custom Questions"
										description="Set how often custom questions should be included in employee pulse surveys."
									/>
								}
								options={customQuestionsFrequencyOptions}
								dangerouslySetClassName="max-w-[197px] ml-auto"
							/>

							<FormSubscribe
								id="customQuestionsFrequencySubscribe"
								selector={(state) => ({
									customQuestionsFrequency: state.values.custom_questions_frequency,
									includeAll: state.values.custom_questions_include_all,
								})}
							>
								{({ customQuestionsFrequency }) => {
									return (
										<FormField<boolean>
											id="custom_questions_include_all"
											labelPosition="right"
											disabled={customQuestionsFrequency === 0}
											label={
												<div
													className={`flex items-center ${customQuestionsFrequency === 0 ? "opacity-disabled" : ""}`}
												>
													<Text size="paragraph-3" weight="regular" color="neutral-9">
														Include all custom questions
													</Text>
													<div className="-mt-2 [&>button]:p-0">
														<Tooltip
															disabled={customQuestionsFrequency === 0}
															content="When selected, pulse surveys will add any active custom questions to the end of the survey as opposed to selecting a randomized subset of custom questions."
														>
															<Icon
																size={12}
																icon={faInfoCircle}
																dangerouslySetClassName="hover:text-neutral-7"
															/>
														</Tooltip>
													</div>
												</div>
											}
											input={(field) => (
												<div className="flex items-center">
													<Checkbox
														id={`custom_questions_include_all-checkbox`}
														checked={customQuestionsFrequency === 0 ? false : field.value}
														onCheckedChange={field.onChange}
														disabled={customQuestionsFrequency === 0}
													/>
												</div>
											)}
										/>
									);
								}}
							</FormSubscribe>
						</div>
						<FormSingleSelect<number>
							id="additional_feedback_question_frequency"
							labelPosition="left"
							justify="between"
							label={
								<SettingLabel
									title="Ask Additional Feedback Question"
									description="Ask an open-ended text question at the end of the pulse survey to gather any additional feedback."
								/>
							}
							options={additionalFeedbackQuestionFrequencyOptions}
							dangerouslySetClassName="max-w-[197px] ml-auto"
						/>
						<FormSubscribe
							id="engagementSettingsSurveySchedule"
							selector={(state) => ({
								customQuestionsFrequency: state.values.custom_questions_frequency,
								customQuestionsIncludeAll: state.values.custom_questions_include_all,
								additionalFeedbackQuestionFrequency:
									state.values.additional_feedback_question_frequency,
								factorsFrequency: state.values.factors_frequency,
								enpsFrequency: state.values.enps_frequency,
								questionCycle: state.values.question_cycle,
							})}
						>
							{(state) => {
								return engagementSettings?.engagement.next_survey_date ? (
									<EngagementSettingsSurveySchedule
										frequency={engagementSettings?.engagement.frequency}
										nextSurveyDate={engagementSettings?.engagement.next_survey_date?.toISOString()}
										questionCycle={state.questionCycle}
										factorsFrequency={state.factorsFrequency}
										enpsFrequency={state.enpsFrequency}
										customQuestionsFrequency={state.customQuestionsFrequency}
										customQuestionsIncludeAll={state.customQuestionsIncludeAll}
										additionalFeedbackQuestionFrequency={state.additionalFeedbackQuestionFrequency}
										sendReminderEmails={engagementSettings?.engagement.send_reminder_emails}
									/>
								) : null;
							}}
						</FormSubscribe>

						<FormSubscribe id="engagementQuestionsUnsavedChanges" selector={(state) => [state?.isDirty]}>
							{([isDirty]) => {
								if (isDirty) {
									setIsUnsafe();
								}
								return null;
							}}
						</FormSubscribe>
					</div>
				</SettingsSection>
			</div>
			<FormSubscribe id="engagementQuestionsUnsavedChangesFooter" selector={(state) => [state?.isDirty]}>
				{([isDirty]) => {
					return (
						<div
							data-form-dirty={isDirty}
							className="group flex w-full justify-end gap-2"
							key="form-footer"
						>
							<div className="w-fit group-data-[form-dirty=false]:hidden">
								<ResetButton color="neutral" variant="outline">
									Discard
								</ResetButton>
							</div>
							<div className="w-fit">
								<SubmitButton
									color="green"
									loading={isSettingCompanyEngagementSettings}
									disabled={isSettingCompanyEngagementSettings || !isDirty}
								>
									Save Changes
								</SubmitButton>
							</div>
						</div>
					);
				}}
			</FormSubscribe>
		</PYForm>
	);
};

export default QuestionsTab;
